/**
 * 官网的纯展示增强：页头滚动态、导航当前区块高亮、区块进入视口时淡入。
 *
 * - 只加 class / 属性，不改任何盒子尺寸与坐标（淡入只动透明度），老内核布局比对用例不受影响；
 * - 没有 IntersectionObserver 的内核（Android 4.4 / 老 X5）直接跳过高亮与淡入，内容一开始就是可见的：
 *   隐藏只在 <html data-reveal="on"> 下生效，而这个属性只有本模块确认能观察之后才会打上；
 * - Chromium 51–57 有 IntersectionObserver 但回调条目没有 isIntersecting（58 才加），按「没有」处理：
 *   ROG 游戏手机 2 自带浏览器就是这种内核，之前只判断了构造函数，区块被隐藏后永远等不到淡入，
 *   整页往下全是空白（#38 第六轮）；
 * - 用户开了「减弱动效」时不做淡入；
 * - 幂等：页面从 loading 切到正文后调用一次即可，重复调用直接返回。
 */

const SectionIds = ['features', 'how-it-works', 'security', 'faq']

let enhanced = false

export function enhancePage(): void {
  if (enhanced) return
  const header = document.querySelector<HTMLElement>('.official-header')
  if (!header) return
  enhanced = true

  watchScrollState(header)

  if (!canObserveIntersection()) return
  highlightCurrentSection()
  if (!prefersReducedMotion()) revealOnScroll()
}

// 构造函数在、且条目带 isIntersecting 才算能用；只有前者的老内核回调里拿到的是 undefined
function canObserveIntersection(): boolean {
  return typeof window.IntersectionObserver === 'function'
    && typeof window.IntersectionObserverEntry === 'function'
    && 'isIntersecting' in window.IntersectionObserverEntry.prototype
}

// 兜底：个别实现 isIntersecting 恒为 false 但比例是对的，二者任一成立都算进入视口
function isInView(entry: IntersectionObserverEntry): boolean {
  return entry.isIntersecting || entry.intersectionRatio > 0
}

function prefersReducedMotion(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

// 离开顶部后页头换成阴影分隔（样式见 .official-header-scrolled）
function watchScrollState(header: HTMLElement): void {
  let ticking = false
  const update = () => {
    ticking = false
    const scrolled = (window.pageYOffset || document.documentElement.scrollTop) > 4
    if (scrolled !== header.classList.contains('official-header-scrolled')) {
      header.classList.toggle('official-header-scrolled', scrolled)
    }
  }
  window.addEventListener('scroll', () => {
    if (ticking) return
    ticking = true
    window.requestAnimationFrame(update)
  }, { passive: true })
  update()
}

// 视口上方 30%–40% 这条带子里是哪个区块，导航就高亮哪个
function highlightCurrentSection(): void {
  const links = new Map<string, HTMLAnchorElement>()
  document.querySelectorAll<HTMLAnchorElement>('.official-nav a[href^="#"]').forEach(a => {
    links.set(a.getAttribute('href')!.slice(1), a)
  })
  const visible = new Set<string>()
  const apply = () => {
    const current = SectionIds.filter(id => visible.has(id)).pop() || ''
    links.forEach((a, id) => {
      if (id === current) a.setAttribute('aria-current', 'location')
      else a.removeAttribute('aria-current')
    })
  }
  const observer = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (isInView(entry)) visible.add(entry.target.id)
      else visible.delete(entry.target.id)
    })
    apply()
  }, { rootMargin: '-30% 0px -60% 0px' })
  SectionIds.forEach(id => {
    const section = document.getElementById(id)
    if (section) observer.observe(section)
  })
}

// 区块进入视口时淡入一次。首屏已经在视口里的元素同样会立刻收到回调，不会停在透明态
function revealOnScroll(): void {
  const targets = document.querySelectorAll<HTMLElement>('.official-reveal')
  if (!targets.length) return
  const observer = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!isInView(entry)) return
      entry.target.classList.add('official-revealed')
      observer.unobserve(entry.target)
    })
  }, { rootMargin: '0px 0px -8% 0px' })
  targets.forEach(el => observer.observe(el))
  document.documentElement.setAttribute('data-reveal', 'on')
}
